import React, {useEffect, useState} from 'react';
import Layout from "../Layout/Layout";
import Typography from "@material-ui/core/Typography";
import Button from "@material-ui/core/Button";
import Explainer from "../../components/UI/forms/Guide/Explainer";
import SortableGrid from "../../components/SortableGrid/SortableGrid";
import ajax, {getErrorMessage} from "../../ajax/ajax";
import VideoOrGalleryDetails, {getDefaultForm, recordedDateIsValid} from "../../components/Upload/VideoOrGalleryDetails";
import {toRecordedDateString} from "../../util/recordedDate";
import Info from "../../components/UI/forms/Guide/Info";
import Message from "../../components/UI/Message/Message";
import {Snackbar} from "@material-ui/core";
import {Alert} from "@material-ui/lab";
import './ImageUpload.scss';
import useGalleryUploader, {maxVideoDurationSeconds} from "../../hooks/useGalleryUploader";
import GalleryUploadProgress from "../../components/Upload/GalleryUploadProgress";

const ImageUpload = () => {
  const [uploaded, setUploaded] = useState([]);
  const [form, setForm] = useState(getDefaultForm());
  const [isPublishing, setIsPublishing] = useState(false);
  const [publishError, setPublishError] = useState(null);
  const [isPublished, setIsPublished] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);

  const {uploads, handleFileEvent, removeUploadingItem, retry, hasPendingMedia} = useGalleryUploader({
    items: uploaded,
    setItems: setUploaded,
  });

  useEffect(() => {
    if (!isPublishing) {
      return;
    }
    // Caught here rather than left to the API, which can only report the derived year as
    // invalid and would send back a message about a field the form no longer shows.
    if (!recordedDateIsValid(form.recordedDate.value)) {
      setIsPublishing(false);
      setPublishError('Bitte ein gültiges Datum angeben.');
      return;
    }
    setSuccessMessage(null);
    ajax.put('/api/v1/gallery', {
      images: uploaded.map((item) => {
        return {
          id: item.databaseId,
          name: item.title,
          isPreview: item.isPreview,
          text: item.text,
        }
      }),
      name: form.name.value,
      recordedDate: toRecordedDateString(form.recordedDate.value),
      description: form.description.value,
      people: form.people.value,
      places: form.places.value,
      categories: form.categories.value,
      tags: form.tags.value,
    }, (data) => {
      setIsPublishing(false);
      setIsPublished(true);
      setPublishError(null);
      setUploaded([]);
      setForm(getDefaultForm());
      setSuccessMessage('Erfolgreich hochgeladen.')
    }, (statusText, status, xhr) => {
      setIsPublishing(false);
      setIsPublished(false);
      setPublishError(getErrorMessage(statusText, status, xhr));
    });
  }, [isPublishing]);

  const hasFiles = Object.values(uploads).length > 0 || uploaded.length > 0;

  return (
    <Layout>
      <div className="ImageUpload">
        <Typography variant="h1" component="h1" gutterBottom>Galerie anlegen</Typography>
        <p style={{marginBottom: '30px'}}>Hier kannst du eine neue Bilder-Galerie auf FamilyTube einstellen.</p>

        <form>
          <Explainer number={1} title="Wähle mehrere Bild-Dateien/Fotos aus" ready done={isPublished}>
            <input id='fileUpload' type='file' multiple accept="image/*,video/*" style={{display: 'none'}} onChange={handleFileEvent} />
            <label htmlFor='fileUpload'>
              <Button variant="contained" color="secondary" size="medium" component="span">{hasFiles ? 'Weitere ' : ''}Bilder auswählen</Button>
            </label>
            <Info>
              Kurze Videos bis {Math.floor(maxVideoDurationSeconds / 60)} Minuten kannst du hier ebenfalls auswählen, sie
              werden dann Teil der Galerie. Längere Videos gehören in den Video-Bereich.
            </Info>
          </Explainer>

          <Explainer number={2} title="Lege die Reihenfolge fest und beschreibe Bilder wenn gewünscht" ready={hasFiles} done={false}>
            <GalleryUploadProgress uploads={uploads} onRemove={removeUploadingItem} onRetry={retry} />
            <SortableGrid items={uploaded} setItems={setUploaded} />
          </Explainer>

          <Explainer number={3} title="Beschreibe die Galerie" ready={uploaded.length > 0} done={isPublished}>
           <VideoOrGalleryDetails form={form} setForm={setForm} fieldsDisabled={isPublishing} nameFieldTitle="Name der Galerie" />
          </Explainer>

          <Explainer number={4} title="Veröffentliche die Galerie" ready={form.name.value.trim() !== ''} last done={isPublished}>
            <Info>Die Galerie ist dann sofort online.</Info>
            {hasPendingMedia ? (
              <Info>
                Ein Video wird noch verarbeitet. Du kannst die Galerie trotzdem schon veröffentlichen, das Video
                erscheint automatisch, sobald es fertig ist.
              </Info>
            ) : null}
            {publishError ? (<Message type="error">{publishError}</Message>) : null}
            <div>
              <Button variant="contained" color="primary" size="large" onClick={() => {setIsPublishing(true);}} style={{marginTop: '15px'}} disabled={isPublishing}>Jetzt veröffentlichen</Button>
            </div>
          </Explainer>
        </form>
        {successMessage ? (<Snackbar open={!!successMessage} autoHideDuration={6000} onClose={() => {setSuccessMessage(null);}}>
          <Alert onClose={() => {setSuccessMessage(null);}} severity="success">{successMessage}</Alert>
        </Snackbar>) : null}
      </div>
    </Layout>
  );
}

export default ImageUpload;
